import { useCallback, useId, useState } from 'react';
import type { FlightSummary, Motor, Rocket } from '@openrocket-web/physics';
import type { Action } from '../state/app';
import type { Analysis } from '../state/derived';
import { configLabel, type MotorConfig } from '../state/configs';
import type { Fmt } from './format';
import { Icon } from './icons';
import { RocketView } from './RocketView';

interface Props {
  readonly rocket: Rocket;
  readonly analysis: Analysis;
  readonly motors: readonly Motor[];
  readonly configs: readonly MotorConfig[];
  readonly activeConfigId: string;
  readonly selectedId: string | undefined;
  readonly fmt: Fmt;
  readonly dispatch: (a: Action) => void;
  readonly onSelect: (id: string) => void;
  readonly onEdit: () => void;
  /** Resumen de la última simulación y si ya no corresponde al diseño actual. */
  readonly flight: { readonly summary: FlightSummary; readonly stale: boolean } | undefined;
}

type View = 'side' | 'back';
type Scale = 'fit' | number;

/** Escalas absolutas del selector (1 = 100 %); «Ajustar» va aparte y muestra su porcentaje. */
const STEPS: readonly number[] = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

/** Giro de la vista: un campo «0°» y un deslizador vertical, como la columna izquierda del dibujo de OpenRocket. */
function RollControl({ roll, onChange }: { readonly roll: number; readonly onChange: (deg: number) => void }): JSX.Element {
  const [draft, setDraft] = useState<string | undefined>();
  const commit = (): void => {
    if (draft !== undefined) {
      const v = Number.parseFloat(draft.replace('°', '').replace(',', '.'));
      if (Number.isFinite(v)) onChange(((Math.round(v) % 360) + 360) % 360);
    }
    setDraft(undefined);
  };
  return (
    <div className="rollctl">
      <input
        type="text"
        inputMode="numeric"
        aria-label="Giro de la vista (grados)"
        value={draft ?? `${roll}°`}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setDraft(undefined);
        }}
      />
      <input type="range" min={0} max={359} step={1} value={roll} aria-label="Deslizador del giro de la vista" aria-valuetext={`${roll} grados`} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

export function DesignView({ rocket, analysis, motors, configs, activeConfigId, selectedId, fmt, dispatch, onSelect, onEdit, flight }: Props): JSX.Element {
  const [view, setView] = useState<View>('side');
  const [scale, setScale] = useState<Scale>('fit');
  const [fitPercent, setFitPercent] = useState(100);
  const [showMarkers, setShowMarkers] = useState(true);
  const [roll, setRoll] = useState(0);
  const ids = { view: useId(), zoom: useId(), motor: useId() };
  const onFit = useCallback((p: number) => setFitPercent((cur) => (cur === p ? cur : p)), []);

  // «Alejar» y «Acercar» pasan al siguiente porcentaje del selector, tomando como actual el ajuste a la ventana si es el caso.
  const current = scale === 'fit' ? fitPercent / 100 : scale;
  const smaller = [...STEPS].reverse().find((v) => v < current - 1e-6);
  const larger = STEPS.find((v) => v > current + 1e-6);

  const { stability, mass, length, refDiameter } = analysis;
  const margin = stability?.margin;
  const marginText =
    margin === undefined || !Number.isFinite(margin) ? '—' : `${margin.toFixed(1)} cal / ${length > 0 ? ((margin * refDiameter * 100) / length).toFixed(1) : '—'} %`;
  const cpKnown = stability !== undefined && Number.isFinite(stability.cpX);
  const stale = flight?.stale === true ? ' (desactualizado)' : '';
  const apogee = flight === undefined ? 'N/A' : `${fmt.distance(flight.summary.apogee)}${stale}`;
  const speed = flight === undefined ? 'N/A' : `${fmt.speedText(flight.summary.maxVelocity)}${stale}`;
  const accel = flight === undefined ? 'N/A' : `${fmt.accelText(flight.summary.maxAcceleration)}${stale}`;

  return (
    <section aria-label="Vista lateral" className="design-view">
      <div className="viewbar" role="toolbar" aria-label="Opciones de la vista">
        <div className="viewbar__group">
          <label htmlFor={ids.view}>Tipo de vista:</label>
          <select id={ids.view} className="viewbar__view" value={view} onChange={(e) => setView(e.target.value as View)}>
            <option value="side">Vista lateral</option>
            <option value="back">Vista trasera</option>
          </select>
        </div>

        <div className="viewbar__group">
          <label htmlFor={ids.zoom}>Zoom:</label>
          <div className="viewbar__row">
            <button type="button" className="iconbtn" aria-label="Alejar" disabled={smaller === undefined} onClick={() => smaller !== undefined && setScale(smaller)}>
              <Icon name="zoom-out" />
            </button>
            <select id={ids.zoom} value={scale === 'fit' ? 'fit' : String(scale)} onChange={(e) => setScale(e.target.value === 'fit' ? 'fit' : Number(e.target.value))}>
              <option value="fit">Ajustar a la ventana ({fitPercent} %)</option>
              {STEPS.map((v) => (
                <option key={v} value={v}>
                  {Math.round(v * 100)} %
                </option>
              ))}
            </select>
            <button type="button" className="iconbtn" aria-label="Acercar" disabled={larger === undefined} onClick={() => larger !== undefined && setScale(larger)}>
              <Icon name="zoom-in" />
            </button>
          </div>
        </div>

        <span className="viewbar__sep" aria-hidden="true" />

        <div className="viewbar__group">
          <span className="viewbar__label" id="stability-label">
            Estabilidad:
          </span>
          <label className="viewbar__check" aria-describedby="stability-label">
            <input type="checkbox" checked={showMarkers} onChange={(e) => setShowMarkers(e.target.checked)} />
            Mostrar CG/CP
          </label>
        </div>

        <span className="viewbar__sep" aria-hidden="true" />

        <div className="viewbar__group">
          <span className="viewbar__label" id="stages-label">
            Etapas:
          </span>
          <div role="group" aria-labelledby="stages-label" className="viewbar__row">
            {/* Un cohete de una sola etapa: el único botón queda pulsado y no se puede soltar. */}
            <button type="button" className="stagebtn" aria-pressed="true" title="Este simulador admite una sola etapa">
              Sustentador
            </button>
          </div>
        </div>

        <div className="viewbar__group viewbar__group--end">
          <label htmlFor={ids.motor}>Configuración del motor:</label>
          <select id={ids.motor} className="viewbar__motor" value={activeConfigId} onChange={(e) => dispatch({ type: 'config-select', id: e.target.value })}>
            {configs.map((c) => (
              <option key={c.id} value={c.id}>
                {configLabel(c, rocket, motors)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="drawing">
        <RocketView
          rocket={rocket}
          cgX={stability?.cgX}
          cpX={stability?.cpX}
          selectedId={selectedId}
          fmt={fmt}
          showMarkers={showMarkers}
          scale={scale}
          view={view}
          roll={roll}
          onFit={onFit}
          onSelect={onSelect}
          onEdit={onEdit}
        />
        <RollControl roll={roll} onChange={setRoll} />

        <div className="drawing__text drawing__text--tl" aria-hidden="true">
          <div>{rocket.name}</div>
          <div>
            Longitud: {fmt.dimension(length)}, Diámetro máximo: {rocket.stack.length === 0 ? 'N/A' : fmt.dimension(refDiameter)}
          </div>
          <div>Masa sin motores: {mass === undefined ? (rocket.stack.length === 0 ? fmt.massText(0) : '—') : fmt.massText(mass.structure.mass)}</div>
          <div>Masa con motores: {mass === undefined ? '—' : fmt.massText(mass.total.mass)}</div>
        </div>

        <div className="drawing__text drawing__text--tr" aria-hidden="true">
          <div>Estabilidad: {marginText}</div>
          <div className="legend">
            <svg width="14" height="14" viewBox="-7 -7 14 14" className="legend__cg">
              <circle r="6" />
              <path d="M0 0 L6 0 A6 6 0 0 1 0 6 Z" />
              <path d="M0 0 L-6 0 A6 6 0 0 1 0 -6 Z" />
            </svg>
            CG: {stability === undefined ? '—' : fmt.dimension(stability.cgX)}
          </div>
          <div className="legend">
            <svg width="14" height="14" viewBox="-7 -7 14 14" className="legend__cp">
              <circle r="6" />
            </svg>
            CP: {cpKnown ? fmt.dimension(stability!.cpX) : '—'}
          </div>
        </div>

        <dl className="drawing__text drawing__text--bl" aria-label="Resultado de la última simulación">
          <div>
            <dt>Apogeo:</dt>
            <dd>{apogee}</dd>
          </div>
          <div>
            <dt>Velocidad máx.:</dt>
            <dd>{speed}</dd>
          </div>
          <div>
            <dt>Aceleración máx.:</dt>
            <dd>{accel}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
